<template>
  <a-drawer
    :visible="visible"
    title="添加推荐方"
    placement="right"
    :width="500"
    :drawerStyle="{ display: 'flex', flexDirection: 'column' }"
    :bodyStyle="{ padding: '16px 20px', flex: 1, overflow: 'hidden' }"
    @close="handleDrawerClose"
  >
    <div class="drawer-content">
      <a-spin :spinning="loading" size="large">
        <div class="drawer-body">
          <template v-if="recommendedList.length">
            <div class="recommend-item" v-for="item in recommendedList" :key="item.id">
              <div class="item-main">
                <!-- <div class="item-title">{{ item.businessName }}</div> -->
                <div class="item-list">
                  <div
                    class="item-row"
                    v-for="(drug, index) in item.recommendedDetailVOList || []"
                    :key="drug.skuId || index"
                  >
                    <div class="drug-left">
                      <div class="drug-info">
                        <div class="drug-name" :title="drug.skuName">{{ drug.skuName }}</div>
                      </div>
                    </div>
                    <span class="drug-count">
                      x{{ (drug.quantity || 1) * (item.recommendedAmount || 1) }}
                    </span>
                  </div>
                  <div
                    class="item-row"
                    v-for="(prescription, index) in item.prescriptionVOList || []"
                    :key="prescription.prescriptionSn || index"
                  >
                    <div class="drug-left">
                      <div class="drug-info">
                        <div class="drug-name" :title="prescription.prescriptionName">
                          {{ prescription.prescriptionName }}
                        </div>
                      </div>
                    </div>
                    <span class="drug-count">
                      x{{ (prescription.quantity || 1) * (item.recommendedAmount || 1) }}
                    </span>
                  </div>
                  <div class="item-action">
                    <a-button type="primary" size="small" @click="handleSelect(item)">
                      选用
                    </a-button>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <a-empty v-else description="暂无数据" />
        </div>
      </a-spin>
    </div>
  </a-drawer>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch, computed } from 'vue'
import { useStore } from 'vuex'
import { getRecommendedList } from '@/api/HealthRecord'
import { message } from 'ant-design-vue'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  diagnosisVOList: {
    type: Array,
    default: () => []
  }
})

// 抽屉关闭事件
const emit = defineEmits(['close'])
// 全局 store
const store = useStore()
// 医生/患者信息（来自预病历 store）
const storeDoctorId = computed(() => store.state.preMedicalRecord?.doctorId ?? null)
const storePatientId = computed(() => store.state.preMedicalRecord?.patientId ?? null)
const storeUcDoctorId = computed(() => store.state.preMedicalRecord?.ucDoctorId ?? null)
const storeUcPatientId = computed(() => store.state.preMedicalRecord?.ucPatientId ?? null)
const patientProfileId = computed(() => store.state.preMedicalRecord?.patientProfileId ?? null)
// 推荐方列表数据
const recommendedList = ref([])
// 加载状态
const loading = ref(false)

// 拉取推荐方列表
const fetchRecommendedList = async () => {
  const params = {
    doctorId: storeDoctorId.value || null,
    ucDoctorId: storeUcDoctorId.value || null,
    diagnosisVOList: props.diagnosisVOList || [],
    patientId: storePatientId.value || null,
    ucPatientId: storeUcPatientId.value || null,
    patientProfileId: patientProfileId.value || null
  }
  try {
    loading.value = true
    const response = await getRecommendedList(params)
    recommendedList.value = Array.isArray(response) ? response : []
    // 处理返回的数据
    console.log('推荐方列表:', response)
  } catch (error) {
    recommendedList.value = []
    console.error('获取推荐方列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 选用推荐方：合并药品/协定方到处方列表
const handleSelect = item => {
  const details = item?.recommendedDetailVOList || []
  const prescriptionList = item?.prescriptionVOList || []
  if (!Array.isArray(details) && !Array.isArray(prescriptionList)) return
  // 仅在诊断为空时写入推荐方携带的诊断（最多 10 条）
  const rawWesternList = item?.diagnosesVOList || item?.diagnosisVOList || []
  if (Array.isArray(rawWesternList) && rawWesternList.length) {
    const currentDiagnosis = store.state.preMedicalRecord?.diagnosisVOList || []
    if (currentDiagnosis.length === 0) {
      store.dispatch('preMedicalRecord/updatePreMedicalRecord', {
        diagnosisVOList: rawWesternList.slice(0, 10)
      })
    }
  }
  // 仅在中医证型为空时写入推荐方携带的证型（最多 10 条）
  if (Array.isArray(item?.chineseDiagnosisVOList) && item.chineseDiagnosisVOList.length) {
    const currentChinese = store.state.preMedicalRecord?.chineseDiagnosisVOList || []
    if (currentChinese.length === 0) {
      store.dispatch('preMedicalRecord/updatePreMedicalRecord', {
        chineseDiagnosisVOList: item.chineseDiagnosisVOList.slice(0, 10)
      })
    }
  }

  // 当前处方列表
  const currentList = store.state.preMedicalRecord?.skuInfoList || []
  const nextList = [...currentList]

  // 限制非协定方和非健康辅助方最多 5 种
  const nonProtocolCodes = new Set(
    currentList
      .filter(entry => entry.skuType !== 7 && entry.skuType !== 4)
      .map(entry => entry.skuCode)
  )
  const nextNonProtocolCodes = new Set(nonProtocolCodes)
  // 合并推荐方明细药品
  details.forEach(detail => {
    if (detail?.skuCode && detail?.productType !== 7 && detail?.productType !== 4) {
      nextNonProtocolCodes.add(detail.skuCode)
    }
  })
  if (nextNonProtocolCodes.size > 5) {
    message.warning('最多添加五种药品')
    return
  }

  details.forEach(detail => {
    const skuCode = detail.skuCode
    if (!skuCode) return
    const multiplier = item?.recommendedAmount || 1
    const existingIndex = nextList.findIndex(entry => entry.skuCode === skuCode)
    if (existingIndex >= 0) {
      const current = nextList[existingIndex]
      nextList[existingIndex] = {
        ...current,
        quantity: (current.quantity || 0) + (detail.quantity || 1) * multiplier
      }
      return
    }
    nextList.push({
      skuCode: detail.skuCode,
      skuName: detail.skuName || '',
      quantity: (detail.quantity || 1) * multiplier,
      // skuId: detail.skuId || null,
      skuType: detail.productType === 5 ? 1 : detail.productType ?? null // 将成药类型 5 归为中西药 1
    })
  })

  // 合并推荐方协定方明细
  prescriptionList.forEach(prescription => {
    const skuCode = prescription.prescriptionSn
    if (!skuCode) return
    const multiplier = item?.recommendedAmount || 1
    const existingIndex = nextList.findIndex(entry => entry.skuCode === skuCode)
    if (existingIndex >= 0) {
      const current = nextList[existingIndex]
      nextList[existingIndex] = {
        ...current,
        quantity: (current.quantity || 0) + (prescription.quantity || 1) * multiplier
      }
      return
    }
    nextList.push({
      skuCode: prescription.prescriptionSn,
      skuName: prescription.prescriptionName || '',
      quantity: (prescription.quantity || 1) * multiplier,
      skuType: 7
    })
  })

  // 回写处方列表到 store
  store.dispatch('preMedicalRecord/updatePreMedicalRecord', {
    skuInfoList: nextList
  })

  message.success(`已加入${item?.businessName || '推荐方'}`)
}

// 关闭抽屉时清空数据
const handleDrawerClose = () => {
  recommendedList.value = []
  loading.value = false
  emit('close')
}

// 抽屉打开时拉取推荐方列表
watch(
  () => props.visible,
  newVal => {
    if (newVal) {
      fetchRecommendedList()
    }
  }
)
</script>

<style scoped lang="scss">
.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  ::v-deep(.ant-spin-nested-loading) {
    overflow: auto;
  }
}
.recommend-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 14px;
  border-radius: 8px;
  border: 1px solid #ffe4c4;
  background: #fff7ef;
  margin-bottom: 12px;
}

.item-main {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-weight: 400;
  font-size: 14px;
  color: #1c1c1c;
  margin-bottom: 6px;
}

.item-sub {
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
}

.item-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #333;
  font-size: 12px;
}

.drug-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.drug-img {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  background: #f5f5f5;
}

.drug-info {
  min-width: 0;
}

.drug-name {
  font-weight: 400;
  font-size: 14px;
  color: #1c1c1c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drug-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 11px;
  color: #ffa940;
}

.label-icon {
  width: 12px;
  height: 12px;
}

.drug-count {
  margin-left: 12px;
  font-weight: 400;
  font-size: 14px;
  color: #333333;
}

.item-action {
  display: flex;
  flex-direction: row-reverse;
  padding-top: 12px;
  ::v-deep(.ant-btn) {
    padding: 0 20px;
    font-weight: 500;
    font-size: 14px;
    color: #8c563f;
    height: 36px;
    border-radius: 8px;
    background-image: linear-gradient(180deg, #fef8ee 0%, #fed7b1 100%);
    border: 1px solid #e6cabf4d;
  }
}
</style>
